import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import LumivetaLogo from '../../components/LumivetaLogo';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import api from '../../services/api';

// Session storage key for 2FA persistence (mobile fix)
const TFA_SESSION_KEY = 'lumiveta_2fa_session';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // 2FA state
  const [show2FA, setShow2FA] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [adminName, setAdminName] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  // MOBILE FIX: Restore 2FA state when user returns from email app
  useEffect(() => {
    const savedSession = sessionStorage.getItem(TFA_SESSION_KEY);
    if (savedSession) {
      try {
        const session = JSON.parse(savedSession);
        // Check if session is still valid (10 min expiry)
        if (session.expiresAt && Date.now() < session.expiresAt) {
          setShow2FA(true);
          setEmail(session.email || '');
          setPassword(session.password || '');
          setTempToken(session.tempToken || '');
          setAdminName(session.adminName || 'Admin');
          console.log('✅ 2FA session restored from storage');
        } else {
          // Session expired, clear it
          sessionStorage.removeItem(TFA_SESSION_KEY);
        }
      } catch (e) {
        sessionStorage.removeItem(TFA_SESSION_KEY);
      }
    }
  }, []);

  // MOBILE FIX: Save 2FA state when it changes
  const save2FASession = (emailVal, passwordVal, tokenVal, nameVal) => {
    const session = {
      email: emailVal,
      password: passwordVal,
      tempToken: tokenVal,
      adminName: nameVal,
      expiresAt: Date.now() + (10 * 60 * 1000) // 10 minutes
    };
    sessionStorage.setItem(TFA_SESSION_KEY, JSON.stringify(session));
  };

  // Clear 2FA session on successful login or cancel
  const clear2FASession = () => {
    sessionStorage.removeItem(TFA_SESSION_KEY);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data = await api.login(email, password);
      
      // Check if 2FA is required (admin account)
      if (data.requires_2fa) {
        setTempToken(data.temp_token);
        setAdminName(data.user?.name || 'Admin');
        setShow2FA(true);
        // MOBILE FIX: Save 2FA state so it persists when user leaves to check email
        save2FASession(email, password, data.temp_token, data.user?.name || 'Admin');
        setLoading(false);
        return;
      }
      
      // Regular user - complete login (NO 2FA)
      if (data.token) {
        clear2FASession();
        // Trigger auth context refresh
        await login(email, password);
        navigate('/main-dashboard');
      }
    } catch (err) {
      setError(err.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data = await api.verify2FA(email, otpCode, tempToken);
      
      if (data.token) {
        // Clear 2FA session on success
        clear2FASession();
        // Store user data and navigate
        localStorage.setItem('lumiveta_user', JSON.stringify(data.user));
        window.location.href = '/main-dashboard';
      }
    } catch (err) {
      setError(err.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResendLoading(true);
    setError('');
    setResendSuccess(false);

    try {
      const data = await api.resend2FA(email, password);
      setTempToken(data.temp_token);
      // Update saved session with new token
      save2FASession(email, password, data.temp_token, adminName);
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 3000);
    } catch (err) {
      setError(err.message || 'Failed to resend code');
    } finally {
      setResendLoading(false);
    }
  };

  const handleBack = () => {
    setShow2FA(false);
    setOtpCode('');
    setTempToken('');
    setError('');
    // Clear saved 2FA session when user cancels
    clear2FASession();
  };

  return (
    <>
      <Helmet>
        <title>{t('signIn')} - Lumiveta</title>
        <meta name="description" content={t('findBiblicalGuidance')} />
      </Helmet>

      <div className="min-h-dvh bg-background flex items-center justify-center p-4 relative">
        {/* EN/ES Language Toggle - Top Right */}
        <div className="absolute top-4 right-4 z-10">
          <div className="flex items-center gap-1 bg-card/80 backdrop-blur-sm rounded-full p-1 border border-border">
            <button
              onClick={() => setLanguage('en')}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                language === 'en'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="login-lang-en"
            >
              EN
            </button>
            <button
              onClick={() => setLanguage('es')}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                language === 'es'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="login-lang-es"
            >
              ES
            </button>
          </div>
        </div>

        <div className="w-full max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left Side - Welcome */}
            <div className="hidden lg:block">
              <div className="max-w-md">
                <LumivetaLogo size="large" className="mb-8" />
                <h1 className="font-heading text-4xl font-bold text-foreground mb-4">
                  <span className="text-gold-gradient">{language === 'es' ? 'Luz' : 'Light'}</span> {language === 'es' ? 'para tu camino' : 'for your path'}
                </h1>
                <p className="text-lg text-muted-foreground mb-8">
                  {t('findBiblicalGuidance')}
                </p>

                <div className="space-y-4">
                  <div className="flex items-start gap-3 glass rounded-lg p-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Icon name="BookOpen" size={20} color="#FFD700" />
                    </div>
                    <div>
                      <h3 className="font-medium text-foreground">{t('scriptureGrounded')}</h3>
                      <p className="text-sm text-muted-foreground">
                        {t('scriptureGroundedDesc')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 glass rounded-lg p-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Icon name="Heart" size={20} color="#FFD700" />
                    </div>
                    <div>
                      <h3 className="font-medium text-foreground">{t('sevenPartResponse')}</h3>
                      <p className="text-sm text-muted-foreground">
                        {t('sevenPartResponseDesc')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 glass rounded-lg p-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Icon name="Shield" size={20} color="#FFD700" />
                    </div>
                    <div>
                      <h3 className="font-medium text-foreground">{t('safeAndPrivate')}</h3>
                      <p className="text-sm text-muted-foreground">
                        {t('safeAndPrivateDesc')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Login Form */}
            <div className="w-full max-w-md mx-auto">
              <div className="glass rounded-2xl border border-primary/20 p-8">
                <div className="text-center mb-8 lg:hidden">
                  <LumivetaLogo size="default" className="justify-center mb-4" />
                </div>

                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/20 mb-4 lg:hidden">
                    <Icon name="Sparkles" size={32} color="#FFD700" />
                  </div>
                  <h2 className="font-heading font-semibold text-2xl text-foreground mb-2">
                    {t('welcomeBack')}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {t('signInToContinue')}
                  </p>
                </div>

                {error && (
                  <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg">
                    <p className="text-sm text-error">{error}</p>
                  </div>
                )}

                {resendSuccess && (
                  <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg">
                    <p className="text-sm text-success">{language === 'es' ? 'Código reenviado' : 'Code resent successfully'}</p>
                  </div>
                )}

                {!show2FA ? (
                  <>
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          {t('emailAddress')}
                        </label>
                        <Input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="your@email.com"
                          required
                          className="bg-input border-border"
                          data-testid="login-email-input"
                        />
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm font-medium text-foreground">
                            {t('password')}
                          </label>
                          <Link
                            to="/forgot-password"
                            className="text-xs text-primary hover:underline"
                          >
                            {t('forgotPassword')}
                          </Link>
                        </div>
                        <div className="relative">
                          <Input
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="••••••••"
                            required
                            className="bg-input border-border pr-10"
                            data-testid="login-password-input"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                            aria-label={showPassword ? (language === 'es' ? 'Ocultar contraseña' : 'Hide password') : (language === 'es' ? 'Mostrar contraseña' : 'Show password')}
                            data-testid="toggle-password-visibility"
                          >
                            <Icon name={showPassword ? "EyeOff" : "Eye"} size={18} />
                          </button>
                        </div>
                      </div>

                      <Button
                        type="submit"
                        variant="default"
                        fullWidth
                        disabled={loading}
                        className="gold-glow mt-6"
                        data-testid="login-submit-btn"
                      >
                        {loading ? t('signingIn') : t('signIn')}
                      </Button>
                    </form>

                    <div className="mt-8 pt-6 border-t border-border text-center">
                      <p className="text-sm text-muted-foreground">
                        {t('dontHaveAccount')}{' '}
                        <Link
                          to="/register"
                          className="text-primary hover:underline font-medium"
                          data-testid="login-create-account-link"
                        >
                          {t('createAccount')}
                        </Link>
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    {/* 2FA Verification Form */}
                    <form onSubmit={handle2FASubmit} className="space-y-4">
                      <div className="glass rounded-lg p-4 mb-4">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                            <Icon name="Shield" size={20} color="#FFD700" />
                          </div>
                          <div>
                            <h3 className="font-medium text-foreground text-sm">
                              {language === 'es' ? 'Verificación de Dos Factores' : 'Two-Factor Authentication'}
                            </h3>
                            <p className="text-xs text-muted-foreground mt-1">
                              {language === 'es' 
                                ? `Hola ${adminName}, hemos enviado un código de 6 dígitos a ${email}`
                                : `Hi ${adminName}, we've sent a 6-digit code to ${email}`
                              }
                            </p>
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          {language === 'es' ? 'Código de Verificación' : 'Verification Code'}
                        </label>
                        <Input
                          type="text"
                          value={otpCode}
                          onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="000000"
                          required
                          maxLength={6}
                          className="bg-input border-border text-center text-2xl tracking-widest font-mono"
                          data-testid="login-2fa-input"
                          autoFocus
                        />
                        <p className="text-xs text-muted-foreground mt-2 text-center">
                          {language === 'es' ? 'El código expira en 10 minutos' : 'Code expires in 10 minutes'}
                        </p>
                      </div>

                      <Button
                        type="submit"
                        variant="default"
                        fullWidth
                        disabled={loading || otpCode.length !== 6}
                        className="gold-glow mt-4"
                        data-testid="login-2fa-submit-btn"
                      >
                        {loading 
                          ? (language === 'es' ? 'Verificando...' : 'Verifying...') 
                          : (language === 'es' ? 'Verificar e Ingresar' : 'Verify & Sign In')
                        }
                      </Button>

                      <div className="flex items-center justify-between mt-4">
                        <button
                          type="button"
                          onClick={handleBack}
                          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                          data-testid="login-2fa-back-btn"
                        >
                          {language === 'es' ? '← Volver' : '← Back'}
                        </button>
                        <button
                          type="button"
                          onClick={handleResendCode}
                          disabled={resendLoading}
                          className="text-sm text-primary hover:underline disabled:opacity-50"
                          data-testid="login-2fa-resend-btn"
                        >
                          {resendLoading 
                            ? (language === 'es' ? 'Enviando...' : 'Sending...') 
                            : (language === 'es' ? 'Reenviar Código' : 'Resend Code')
                          }
                        </button>
                      </div>
                    </form>
                  </>
                )}
              </div>

              <p className="text-center text-xs text-muted-foreground mt-6">
                {t('copyrightFull')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
